import { OnInit, OnDestroy, Component, ElementRef, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { ApplicationData } from 'src/app/data/application.data';
import { SheetProperties } from 'src/app/data/configurator/sheet/sheet-properties.data';
import { Rectangle } from 'src/app/tools/rectangle';
import { RatioUtil } from 'src/app/tools/ratio.utils';
import { Label } from 'src/app/display/label';
import { LabelData } from 'src/app/data/configurator/label/label.data';
import { ResizedEvent } from 'angular-resize-event';
import { Subscription } from 'rxjs';
import { ConfigurationEvent } from 'src/app/events/configuration.event';

@Component({
  selector: 'sheet-preview',
  template: `
    <div class="sheet-preview__container" (resized)="onResized($event)">
      <div class="sheet-preview__content" #sheet>
        <div *ngFor="let item of items; let i = index"
          class="sheet-preview__label"
          [ngClass] = "{
            'sheet-preview__label--selected': item.labelData == applicationData.configuration.currentLabel || !interactive
          }"
          [ngStyle] = "{
              left: item.left + 'px',
              top: item.top + 'px'
          }"
        >
          <label-preview
            [ratio]="ratio"
            [label] = "item.labelData"
            [ngStyle] = "{
              transform: 'scale(' + item.scale + ') rotate('+ (item.rotation*-1) + 'deg)'
            }"
            (click)="selectLabel(item.labelData)"
          ></label-preview>
        </div>
        <ng-container *ngFor="let item of items; let i = index">
          <input
            *ngIf="duplicateMode && item.labelData != reference && item.labelData.templateData.id == reference.templateData.id"
            type="checkbox"
            (change) = "updateSelected(item.labelData, $event.currentTarget.checked)"
            class="sheet-preview__checkbox"
            [(ngModel)]="item.selected"
            [ngStyle] = "{
                left: item.checkboxLeft + 'px',
                top: item.checkboxTop + 'px'
            }"
          >
        </ng-container>
      </div>
    </div>
  `,
  host: {
    class: 'sheet-preview'
  }
})
export class SheetPreview implements OnInit, OnDestroy {


  // Width
  @Input() set width(value: number) {
    // this._width = value;
    // this.redraw();
  }
  private _width: number = 0;

  // Height
  @Input() set height(value: number) {
    // this._height = value;
    // this.redraw();
  }
  private _height: number = 0;

  // index
  @Input() set index(value: number) {
    this._index = value;
    this.redraw();
  }
  private _index: number = 0;

  // interactive
  @Input() set interactive(value: boolean) {
    this._interactive = value;
  }
  get interactive():boolean {
    return this._interactive;
  }
  private _interactive: boolean = false;

  // ratio
  @Input() public set ratio(value: number) {
    this._ratio = value;
  }
  public get ratio(): number {
    return this._ratio;
  }
  private _ratio: number = 1;

  // duplicateMode
  @Input() public set duplicateMode(value: boolean) {
    this._duplicateMode = value;
  }
  public get duplicateMode(): boolean {
    return this._duplicateMode;
  }
  private _duplicateMode: boolean = false;

  // duplicateMode
  @Input() public set selected(value: Array<LabelData>) {
    this._selected = value;
  }
  public get selected(): Array<LabelData> {
    return this._selected;
  }
  private _selected: Array<LabelData> = [];

  // interactive
  @Input() public set reference(value: LabelData) {
    this._reference = value;
  }
  public get reference(): LabelData {
    return this._reference;
  }
  private _reference: LabelData;

  public size: Rectangle;
  public items: Array<any>;

  protected _subscription: Subscription;
  private _redrawTimeout: any;

  @Output() onLabelSelected = new EventEmitter<LabelData>();

  @ViewChild('sheet') public sheet: ElementRef<any>;


  /**
   * Get selected labels
   */
  public get selectedLabels(): Array<LabelData> {
    return this.items.filter(item => item.selected).map(item => item.labelData);
  }

	/**
	 * Constructor
	 */
  constructor(
    private applicationData: ApplicationData,
    private elementRef: ElementRef
  ) {

  }

	/**
	 * On init
	 */
  ngOnInit() {
    this.redraw();

    this._subscription = this.applicationData.configuration.onChange.subscribe({
      next: (event: ConfigurationEvent) => {
        if (this._redrawTimeout) {
          clearTimeout(this._redrawTimeout);
        }
        this._redrawTimeout = setTimeout(() => {
          this._redrawTimeout = null;
          this.redraw();
        }, 60);

      }
    });
  }

  /**
   * On resize
   */
  public onResized(event: ResizedEvent) {
    // console.log(event);
    event.newRect.width
    if (event.newRect.width > 0 && event.newRect.height > 0) {
      this._height = event.newRect.height;
      this._width = event.newRect.width;
      this.redraw();
    }
  }

  /**
	 * Redraw
	 */
  redraw() {
    this.cleanup();

    if (this._width == 0 || this._height == 0) {
      return;
    }

    var data: SheetProperties = this.applicationData.configuration.sheetProperties;

    if (data.width <= data.height) { // portrait
      this.size = RatioUtil.scaleToFit(new Rectangle(0, 0, data.pixelsWidth, data.pixelsHeight), new Rectangle(0, 0, this._width, this._height));
    } else { // landscape
      this.size = RatioUtil.scaleToFit(new Rectangle(0, 0, data.pixelsWidth, data.pixelsHeight), new Rectangle(0, 0, this._width, this._height));
    }

    var ratio: number = this.size.height / data.pixelsHeight;

    // console.log(ratio, data.pixelsWidth, data.pixelsHeight)

    this.sheet.nativeElement.style.width = this.size.width + 'px';
    this.sheet.nativeElement.style.height = this.size.height + 'px';

    var pointer: number;
    for (let i: number = 0; i < data.layouts.length; i++) {
      pointer = this._index * data.layouts.length + i;

      if (this.applicationData.configuration.labels[pointer]) {
        // console.log(data.layouts[i]);
        this.items.push({
          labelData: this.applicationData.configuration.labels[pointer],
          left: (data.layouts[i].pixelsX * ratio),
          top: (data.layouts[i].pixelsY * ratio),
          checkboxLeft: (data.layouts[i].checkBoxX * ratio),
          checkboxTop: (data.layouts[i].checkBoxY * ratio),
          scale: (ratio / Label.PREVIEW_RATIO) / this.ratio,
          rotation: -data.layouts[i].rotation,
          selected: this.selected.indexOf(this.applicationData.configuration.labels[pointer]) >= 0
        });
      }
    }
  }

  /**
   * Select label
   */
  public selectLabel(labelData: LabelData): void {
    this.onLabelSelected.emit(labelData);
  }

  /**
   * Update selected
   */
  updateSelected(labelData:LabelData, value:any):void {

    if (value) {
      this.selected.push(labelData);
    } else {
      this.selected = this.selected.filter(item => item != labelData);
    }

  }

  /**
   * Reset
   */
  reset() {
    for (let item of this.items) {
      item.selected = false;
    }

    // console.log("reset", this.items);
  }

  /**
   * On destroy
   */
  cleanup() {
    this.items = [];
  }

	/**
	* On destroy
	*/
  ngOnDestroy() {
    this.cleanup();

    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
  }
}
